import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Observable, catchError, distinctUntilChanged, map, of } from 'rxjs';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_VALIDATORS, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { FormComponent, FormMode, formlyMatButtonNames } from '@app/shared/forms/components';
import { BankEntity } from '@app/shared/models/bank';
import { BankService, UtilsService } from '@app/shared/services';
import { mapArrayToSelectOptions } from '@app/utils/forms';
import { BankBaseTabPageComponent } from '../bank-base-tab-page.component';
import { BankDetailTabs } from '../bank-detail-tabs.constant';

const SQL_SERVER_MAX_INT: number = 2**31 - 1;

@Component({
	selector: 'bank-profile',
	standalone: true,
	imports: [FormComponent],
	templateUrl: './bank-profile.component.html',
	styleUrls: ['./bank-profile.component.less'],
})
export class BankProfileComponent extends BankBaseTabPageComponent implements OnInit {
	@Input() entity: BankEntity;
	@Output() entityChange: EventEmitter<BankEntity> = new EventEmitter<BankEntity>();
	@ViewChild('bankProfileForm') private readonly formComponent: FormComponent;

	private revertEntity: string;
	private readonly destroyRef = inject(DestroyRef);

	form: UntypedFormGroup = new UntypedFormGroup({});
	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: this.readOnly,
			selects: {
				statuses: [],
			},
		},
	};
	fields: FormlyFieldConfig[] = [];

	constructor(
		private readonly bankService: BankService,
		private readonly utilsService: UtilsService,
	) {
		super();
	}

	ngOnInit(): void {
		this.revertEntity = JSON.stringify(this.entity);
		this.loadOptions();
	}

	private loadOptions(): void {
		this.utilsService.getBankStatuses().subscribe((statuses) => {
			this.options.formState.selects.statuses = mapArrayToSelectOptions(statuses.bankStatuses, {
				value: 'Id',
				label: 'Description',
				all: false,
				sort: 'label',
			});

			this.loadEntityDetail();
		});
	}

	private loadEntityDetail(): void {
		this.fields = this.getFields();
		// NOTE: Some issue with the timing of how the FormComponent is loaded means that the [form] object is not
		// being copied to viewForm, so we need to reference the component directly to access the change detection.
		this.formComponent.viewForm.valueChanges.pipe(
			map(() => this.formComponent.viewForm.dirty),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyRef),
		).subscribe((isDirty) => {
			if (isDirty) {
				this.onChanges();
			} else {
				this.tabChange.emit(null);
			}
		});
	}

	onChanges(): void {
		this.tabChange.emit({
			tabName: BankDetailTabs.BankProfile,
			cancelChanges: () => this.cancelChanges(),
			saveChanges: () => this.saveChanges(),
			isValid: () => this.formComponent.viewForm.valid,
		});
	}

	cancelChanges(): void {
		this.entity = JSON.parse(this.revertEntity) as BankEntity;
		this.entityChange.emit(this.entity);
		this.formComponent.viewForm.reset(this.entity);
	}

	saveChanges(): Observable<boolean> {
		this.formComponent.viewForm.updateValueAndValidity();

		if (this.formComponent.viewForm.invalid) {
			this.formComponent.viewForm.markAllAsTouched();
			return of(false);
		}

		return this.bankService.updateEntity(this.entity).pipe(
			map((data: BankEntity) => {
				this.entity = data;
				this.entityChange.emit(this.entity);
				this.revertEntity = JSON.stringify(data);
				this.formComponent.viewForm.reset(this.entity);
				return true;
			}),
			catchError(() => of(false)),
		);
	}

	private getFields(): FormlyFieldConfig[] {
		return [
			{
				wrappers: [FORMLY_FIELD_WRAPPERS.basicWrapper],
				props: {
					class: 'bank-profile-container',
				},
				fieldGroup: [
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'ShortName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-3',
								props: {
									label: 'Bank Code',
									placeholder: 'Enter bank code',
									required: true,
									maxLength: 10,
									disabled: this.readOnly,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
								},
							},
							{
								key: 'BankName',
								type: FORMLY_FIELD_TYPES.input,
								className: 'col-xs-9',
								props: {
									label: 'Bank Name',
									placeholder: 'Enter bank name',
									required: true,
									maxLength: 30,
									disabled: this.readOnly,
								},
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.hasBegEndWhitespace],
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'BankStatusId',
								type: FORMLY_FIELD_TYPES.select,
								className: 'col-xs-3',
								props: {
									label: 'Status',
									required: false,
								},
								expressions: {
									'props.options': 'formState.selects.statuses',
									'props.disabled': () => this.entity.IsDefaultBank || this.readOnly,
								},
							},
							{
								type: FORMLY_FIELD_TYPES.formlyMatButton,
								props: {
									label: '',
									matButtonName: formlyMatButtonNames.MatIconButton,
									buttonClassArray: ['info-icon'],
									tooltip: 'The status cannot be changed for the default bank.',
									icon: 'info',
								},
								expressions: {
									hide: () => this.readOnly || !this.entity.IsDefaultBank,
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'AccountNumber',
								type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
								className: 'col-xs-4',
								props: {
									label: 'Account Number',
									placeholder: 'Enter account number',
									required: true,
									maxLength: 17,
									disabled: this.readOnly,
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								type: FORMLY_FIELD_TYPES.micrInput,
								key: 'FormattedBankMICRNumber',
								props: {
									label: 'Formatted Account Number',
									placeholder: 'Enter MICR formatted account number',
									maxLength: 17,
									hideMicrKey: this.readOnly,
									disabled: this.readOnly,
								},
								className: 'col-xs-5',
							},
							{
								type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
								key: 'TransitNumber',
								props: {
									label: 'Routing Number',
									maxLength: 9,
									disabled: this.readOnly,
								},
								className: 'col-xs-3',
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.routingNumber],
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								type: FORMLY_FIELD_TYPES.micrInput,
								key: 'FormattedDepositMICRNumber',
								props: {
									label: 'Formatted Deposit Number',
									placeholder: 'Enter MICR formatted deposit number',
									maxLength: 12,
									hideMicrKey: this.readOnly,
									disabled: this.readOnly,
								},
								className: 'col-xs-5',
							},
							{
								type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
								key: 'DepositTransitNumber',
								props: {
									label: 'Deposit Routing Number',
									maxLength: 9,
									disabled: this.readOnly,
								},
								className: 'col-xs-3',
								validators: {
									validation: [FORMLY_FIELD_VALIDATORS.routingNumber],
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'NextCheckNumber',
								type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
								className: 'col-xs-3',
								props: {
									label: 'Next Check #',
									type: 'number',
									maxLength: 10,
									max: SQL_SERVER_MAX_INT,
									disabled: this.readOnly,
								},
							},
							{
								key: 'NextDepositNumber',
								type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
								className: 'col-xs-3',
								props: {
									label: 'Next Deposit #',
									type: 'number',
									maxLength: 10,
									max: SQL_SERVER_MAX_INT,
									disabled: this.readOnly,
								},
							},
						],
					},
				],
			},
		];
	}
}
